import axios, { AxiosError, AxiosResponse } from 'axios';
import { API_VERSION, ROOT_URL } from 'constants/index';
import { Identifier } from 'types/types';
import {
  FieldTemplate,
  Patient,
  User,
  normalize,
  normalizeOne,
} from './models';
import { CodeLibraryOption, ProjectCodeLibrary } from './models/CodeLibrary';
import i18next from 'i18next';
import { PermissionArray } from 'state/permissionState';
import { Condition, ConditionGroup } from './models/FieldTemplate';
import { ExportDownloadFormat } from 'routes/Export/ResultList';

export const logIn = async (
  userId: string,
  password: string,
): Promise<AxiosResponse | AxiosError> => {
  try {
    const response = await axios.post(`${ROOT_URL}/${API_VERSION}/user/login`, {
      userId,
      password,
    });
    axios.defaults.headers.common[
      'Authorization'
    ] = `Bearer ${response.data.data.accessToken}`;
    localStorage.setItem('token', response.data.data.accessToken);

    return response;
  } catch (err: unknown) {
    return err as AxiosError;
  }
};

export const resetPassword = async (
  userId: string,
): Promise<AxiosResponse | AxiosError> => {
  try {
    const response = await axios.post(
      `${ROOT_URL}/${API_VERSION}/user/resetPassword`,
      {
        userId,
      },
    );
    return response;
  } catch (err: unknown) {
    return err as AxiosError;
  }
};

export const getAvailablePatientFields = async (): Promise<string[]> => {
  const response = await axios.get(
    `${ROOT_URL}/${API_VERSION}/config/availablePatientFields`,
  );

  return response?.data?.data ?? [];
};

export const getPatientByIdentifier = async (
  projectId: string | undefined,
  identifier: Identifier,
): Promise<Patient> => {
  const response = await axios.get(
    `${ROOT_URL}/${API_VERSION}/projects/${projectId}/patients/byIdentifier/${identifier.type}/${identifier.id}`,
  );
  return response.data?.data?.attributes;
};

export const updatePassword = async (body: {
  userId?: string;
  newPassword: string;
  passwordResetToken: string | null;
}): Promise<AxiosResponse | AxiosError> => {
  try {
    const response = await axios.post(
      `${ROOT_URL}/${API_VERSION}/user/updatePassword`,
      body,
    );
    return response;
  } catch (err: unknown) {
    return err as AxiosError;
  }
};

export const getNextPatientId = async (
  projectId: string | undefined,
): Promise<string> => {
  try {
    const response = await axios.get(
      `${ROOT_URL}/${API_VERSION}/projects/${projectId}/patients/nextPatientProjectId`,
    );
    return response?.data?.data?.nextPatientProjectId ?? '';
  } catch (err: any) {
    return err;
  }
};

export const getSessionUser = async (): Promise<User> => {
  try {
    const response = await axios.get(`${ROOT_URL}/${API_VERSION}/users/me`);
    return normalizeOne(response?.data?.data);
  } catch (err: any) {
    return err;
  }
};

export const getPermissions = async (
  projectId?: string,
): Promise<PermissionArray> => {
  try {
    const response = await axios.post(`${ROOT_URL}/${API_VERSION}/user/token`, {
      projectId,
    });

    axios.defaults.headers.common[
      'Authorization'
    ] = `Bearer ${response.data.data.accessToken}`;
    localStorage.setItem('token', response.data.data.accessToken);
    localStorage.setItem('refresh-token', response.data.data.refreshToken);

    return response?.data?.data?.permissions ?? [];
  } catch (err: any) {
    return [];
  }
};

export const getFieldAlias = async ({
  projectId,
  title,
  usedAliases,
}: {
  projectId?: string;
  title?: string;
  usedAliases: string[];
}): Promise<string> => {
  const response = await axios.post(
    `${ROOT_URL}/${API_VERSION}/projects/${projectId}/availableFieldAlias`,
    {
      title,
      usedAliases,
    },
  );
  return response?.data?.data?.alias;
};

export enum AliasValidationStatus {
  ALIAS_IS_ALREADY_USED = 'ALIAS_IS_ALREADY_USED',
  SUCCESS = 'SUCCESS',
}
interface ValidateAliasProps {
  newAlias?: string;
  status: AliasValidationStatus;
}
export const validateFieldAlias = async ({
  projectId,
  alias,
  usedAliases,
}: {
  projectId?: string;
  alias?: string;
  usedAliases?: string[];
}): Promise<ValidateAliasProps> => {
  const response = await axios.post(
    `${ROOT_URL}/${API_VERSION}/projects/${projectId}/validateFieldAlias`,
    {
      alias,
      usedAliases,
    },
  );
  return response?.data?.data;
};

export async function getProjectCodeLibraries(
  projectId?: string,
): Promise<ProjectCodeLibrary[]> {
  const response = await axios.get(
    `${ROOT_URL}/${API_VERSION}/projects/${projectId}/fieldTemplates/optionsSources`,
  );

  return response?.data?.data;
}

export const getProjectCodeLibraryOptions = async ({
  projectId,
  codeLibraryId,
  optionsIds,
}: {
  projectId?: string;
  codeLibraryId?: string;
  optionsIds: (string | null)[];
}): Promise<CodeLibraryOption[]> => {
  const response = await axios.get(
    `${ROOT_URL}/${API_VERSION}/projects/${projectId}/codeLibraries/${codeLibraryId}/options/${optionsIds.join(
      ',',
    )}`,
  );

  return response?.data?.data.map((el: any) => {
    return {
      ...el.attributes,
      id: el.id,
    };
  });
};

export async function fieldsReferingToCurrentField({
  projectId,
  alias,
}: {
  projectId?: string;
  alias: string;
}): Promise<FieldTemplate[]> {
  const response = await axios.get(
    `${ROOT_URL}/${API_VERSION}/projects/${projectId}/fieldTemplates/optionsSources/alias/${alias}`,
  );

  return response?.data?.data;
}

export async function getCalculatedFieldValue({
  formId,
  fieldTemplateId,
  visitId,
}: {
  formId: string;
  fieldTemplateId: string;
  visitId: string;
}): Promise<{
  value?: string;
  errorMessage?: string;
}> {
  try {
    const response = await axios.get(
      `${ROOT_URL}/${API_VERSION}/visits/${visitId}/forms/${formId}/calculateField/${fieldTemplateId}`,
    );

    return {
      value: response?.data?.data?.result,
    };
  } catch (err: any) {
    const error = err?.response?.data?.error?.[0];
    const code = error?.code;
    const syntaxError = error?.additionalInfo?.errorMessage ?? '';
    const errorStatus = error?.additionalInfo.errorStatus ?? '';
    let errorMessage = i18next.t('forms.errors.calculatedFieldError');
    const potentialErrorStatuses = [
      'ERR_PHP_RUNTIME_ERROR',
      'ERR_PHP_SYNTAX_ERROR',
    ];
    if (code === '10.61' && potentialErrorStatuses.includes(errorStatus)) {
      errorMessage = i18next.t('forms.errors.calculatedFieldSyntaxError', {
        syntaxError,
      });
    }

    return {
      errorMessage,
    };
  }
}

export async function getFieldTemplateByAlias({
  projectId,
  alias,
}: {
  projectId?: string;
  alias: string;
}): Promise<FieldTemplate> {
  const response = await axios.get(
    `${ROOT_URL}/${API_VERSION}/projects/${projectId}/fieldTemplates/alias/${alias}`,
  );

  return response?.data?.data?.[0]?.attributes;
}

export async function getFormTeplateTypeForExport({
  projectId,
  formTemplateTypeId,
}: {
  projectId?: string;
  formTemplateTypeId: string;
}): Promise<any> {
  const response = await axios.get(
    `${ROOT_URL}/${API_VERSION}/projects/${projectId}/formTemplateTypes/${formTemplateTypeId}/exported`,
  );

  return response?.data;
}

export async function importFormTemplateType({
  projectId,
  data,
}: {
  projectId?: string;
  data: any;
}): Promise<any> {
  const response = await axios.post(
    `${ROOT_URL}/${API_VERSION}/projects/${projectId}/formTemplateTypes/import`,
    data,
  );

  return response?.data;
}

export async function getProjectsFieldTemplates({
  projectId,
  publishedOnly,
}: {
  projectId?: string;
  publishedOnly?: boolean;
}): Promise<FieldTemplate[]> {
  const response = await axios.get(
    `${ROOT_URL}/${API_VERSION}/projects/${projectId}/fieldTemplates?pageSize=4500&latestOnly=true${
      publishedOnly ? '&publishedOnly=true' : ''
    }`,
  );

  const fields = (response?.data?.data ?? []).map((el: any) => {
    return {
      ...el.attributes,
      id: el.id,
    };
  }) as FieldTemplate[];

  return fields;
}

export async function exportPatients({
  projectId,
  patientFilters,
}: {
  projectId?: string;
  patientFilters: Condition[];
}): Promise<any> {
  const response = await axios.post(
    `${ROOT_URL}/${API_VERSION}/projects/${projectId}/export/patients`,
    {
      patientFilter: {
        type: 'conditionGroup',
        operation: 'and',
        conditions: patientFilters,
      },
    },
  );

  return response?.data;
}

type ExportField =
  | {
      fieldTemplateId: string;
      formTemplateId: string;
      type: string;
    }
  | {
      type: string;
      attribute: string;
    };

export async function startExport({
  projectId,
  patientFilter,
  format,
  fields,
  visitFilter = '*',
  statusFilter,
}: {
  projectId?: string;
  patientFilter: ConditionGroup;
  format: ExportDownloadFormat;
  fields: ExportField[];
  visitFilter?: string;
  statusFilter?: string[];
}): Promise<any> {
  const response = await axios.post(
    `${ROOT_URL}/${API_VERSION}/projects/${projectId}/export/processes`,
    {
      visitFilter,
      statusFilter,
      format,
      patientFilter,
      fields,
    },
  );

  return response?.data?.data;
}

export async function getExportStatus({
  projectId,
  processId,
}: {
  projectId?: string;
  processId?: string;
}): Promise<any> {
  const response = await axios.get(
    `${ROOT_URL}/${API_VERSION}/projects/${projectId}/export/processes/${processId}`,
  );

  return response?.data?.data;
}

export async function getFormsComments({ formId }: { formId?: string }) {
  const response = await axios.get(
    `${ROOT_URL}/${API_VERSION}/comments/form/${formId}`,
  );

  return normalize(response?.data);
}

export async function getFieldsHistory({
  formId,
  fieldTemplateId,
}: {
  fieldTemplateId?: string;
  formId?: string;
}) {
  const response = await axios.get(
    `${ROOT_URL}/${API_VERSION}/comments/form/${formId}/fieldTemplate/${fieldTemplateId}/history`,
  );

  return normalize(response?.data);
}

// fills data for a form
export async function uploadHl7({
  formId,
  hl7Text,
  visitId,
}: {
  formId: string;
  hl7Text: string;
  visitId: string;
}): Promise<{
  value?: string;
  errorMessage?: string;
}> {
  const response = await axios.post(
    `${ROOT_URL}/${API_VERSION}/visits/${visitId}/forms/${formId}/importHl7`,
    hl7Text,
    { headers: { 'Content-Type': 'text/plain' } },
  );
  return response?.data?.data;
}

export async function importHl7({
  projectId,
  hl7Text,
  rewritePatientData,
}: {
  projectId: string;
  hl7Text: string;
  rewritePatientData?: boolean;
}): Promise<{
  value?: string;
  errorMessage?: string;
}> {
  const response = await axios.post(
    `${ROOT_URL}/${API_VERSION}/projects/${projectId}/importHl7`,
    hl7Text,
    { headers: { 'Content-Type': 'text/plain' } },
  );
  return response?.data?.data;
}

export async function clearProjectData(projectId: string) {
  const response = await axios.post(
    `${ROOT_URL}/${API_VERSION}/projects/${projectId}/clearProjectData`,
  );

  return response?.data;
}
